/**
 * Smart Polling System
 * Only calls APIs when absolutely necessary based on:
 * 1. Rate limit availability
 * 2. Data freshness requirements
 * 3. API-specific polling intervals calculated from daily limits
 */

import { rateLimiters } from './rateLimiter';
import { getAllOptimalIntervals, validateIntervals, getDailyUsageProjections } from './intervalCalculator';
import { shouldCallAPI, getCacheStatus } from './smartCaching';

// Calculate optimal polling intervals based on daily limits (lazy-loaded)
let calculatedIntervals = null;

function getPollingIntervals() {
  if (!calculatedIntervals) {
    calculatedIntervals = getAllOptimalIntervals();
    
    // Validate intervals and log results
    const validation = validateIntervals(calculatedIntervals);
    console.log('📊 API Interval Validation:', validation);

    const projections = getDailyUsageProjections(calculatedIntervals);
    console.log('📈 Daily Usage Projections:', projections);
  }
  return calculatedIntervals;
}

// Export the calculated intervals
export const POLLING_INTERVALS = getPollingIntervals();

// Track last successful fetch for each API
const lastFetchTimes = {
  morpho: 0,
  coingecko: 0,
  etherscan: 0,
  coinglass: 0,
  defillama: 0
};

/**
 * Check if an API should be called based on:
 * - Data freshness (smart caching)
 * - Time since last fetch
 * - Rate limit availability
 */
export function shouldFetchAPI(apiName) {
  // First check if data is still fresh (smart caching)
  if (!shouldCallAPI(apiName)) {
    return false;
  }
  
  const now = Date.now();
  const lastFetch = lastFetchTimes[apiName] || 0;
  const interval = POLLING_INTERVALS[apiName];
  
  // Check if enough time has passed
  if (now - lastFetch < interval) {
    const waitTime = Math.ceil((interval - (now - lastFetch)) / 1000);
    console.log(`⏱️  ${apiName}: Too soon to fetch (wait ${waitTime}s)`);
    return false;
  }
  
  // Check rate limit availability
  const limiter = rateLimiters[apiName];
  if (limiter && !limiter.canMakeRequest()) {
    console.log(`🚫 ${apiName}: Rate limited, skipping fetch`);
    return false;
  }
  
  console.log(`✅ ${apiName}: OK to fetch`);
  return true;
}

/**
 * Record successful API fetch
 */
export function recordFetch(apiName) {
  lastFetchTimes[apiName] = Date.now();
}

/**
 * Get time until next allowed fetch
 */
export function getNextFetchTime(apiName) {
  const now = Date.now();
  const lastFetch = lastFetchTimes[apiName] || 0;
  const interval = POLLING_INTERVALS[apiName];
  const nextFetch = lastFetch + interval;
  
  if (nextFetch <= now) {
    return 0; // Can fetch now
  }
  
  return Math.ceil((nextFetch - now) / 1000); // Seconds until next fetch
}

/**
 * Get all fetch status
 */
export function getAllFetchStatus() {
  return Object.keys(POLLING_INTERVALS).map(apiName => ({
    api: apiName,
    canFetch: shouldFetchAPI(apiName),
    nextFetchIn: getNextFetchTime(apiName),
    interval: POLLING_INTERVALS[apiName] / 1000 / 60 // Convert to minutes
  }));
}

